import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const DEFAULT_EXPORT_TIMEOUT_MS = 10 * 60 * 1000;

export interface UseExportDownloadResult {
  isExporting: boolean;
  setIsExporting: (value: boolean) => void;
  exportError: string | null;
  setExportError: (value: string | null) => void;
  isExportingRef: MutableRefObject<boolean>;
  downloadFile: (url: string, filename: string) => Promise<void>;
}

/**
 * Shared export/download state for the editors (short video + audiogram).
 *
 * Centralizes the bits that were previously copy-pasted: an `isExporting`
 * flag (mirrored into a ref for use inside socket handlers), an `exportError`
 * message, a hardened `downloadFile` helper (fetch + blob + revokeObjectURL),
 * and a safety timeout so the "Exporting…" UI can never hang forever.
 */
export function useExportDownload(
  timeoutMs: number = DEFAULT_EXPORT_TIMEOUT_MS,
): UseExportDownloadResult {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const isExportingRef = useRef(false);

  useEffect(() => {
    isExportingRef.current = isExporting;
  }, [isExporting]);

  useEffect(() => {
    if (!isExporting) return undefined;
    const timer = setTimeout(() => {
      setIsExporting(false);
      setExportError(
        "Export is taking longer than expected. Please check back later or try again.",
      );
    }, timeoutMs);
    return () => clearTimeout(timer);
  }, [isExporting, timeoutMs]);

  const downloadFile = useCallback(async (url: string, filename: string) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download failed (${response.status})`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);
  }, []);

  return {
    isExporting,
    setIsExporting,
    exportError,
    setExportError,
    isExportingRef,
    downloadFile,
  };
}
